import React, { useState, useEffect } from 'react';
import { default as getLabelFromStorage } from "@pb/utils/labels/getLabelFromStorage";
import { trackOptimizelyVanillaEvent } from "@pb/utils/optimizely";
import { default as isTouchDevice } from "@pb/utils/isTouchDevice";
import cn from 'classnames';
import ProductCarousel from "../../../../compositions/ProductCarousel";
import IntersectionVisible from "../../../../helpers/IntersectionVisible";
import constants, { InternalBrandIds } from '../../constants';
import { AbEvents } from '../../helpers/optimizely/optimizelyConstants';
import { handleClick, handleClickForBrand, triggerCarouselImpression } from '../../helpers/tracking';
import styles from './CarouselV1.module.scss';
const uiLabels = {
    next: getLabelFromStorage('general.next'),
    prev: getLabelFromStorage('general.previous'),
};
const Carousel = ({ className, praConfiguration, carouselTitle, modelInfo = '', finalProducts, articleId, imageType, origin = '', showDotsWithArrows = true, textNext, textPrev, setTrackedArticles, trackedArticles, isLoading, breakpoint, carouselHeadingClassName, }) => {
    const [trackedProductsOnImpression, setTrackedProductsOnImpression] = useState([]);
    const [carouselIsShown, setCarouselIsShown] = useState(false);
    const initialImpressionParams = {
        praConfiguration,
        carouselTitle,
        origin,
        modelInfo,
    };
    const { RESOLVER: resolverSettings, ID, BRAND, NUM_OF_ITEMS_PER_SLIDE, THRESHOLD, DESCRIPTION_SIZE, HEADING_SIZE, CENTER_ALIGNED, PRODUCT_TITLE_TRUNCATE, LAZYLOAD_IMAGES, } = praConfiguration;
    const disablePadding = process.env.IS_PRA_CAROUSEL_PADDING_DISABLED === 'enabled';
    const isTitleCenterAligned = process.env.IS_PRA_TITLE_CENTRE_ALIGN_DISABLED !== 'enabled';
    const isDotsAboveCarousel = process.env.IS_PRA_DOTS_ABOVE_CAROUSEL === 'enabled';
    const isArrowWithBackground = process.env.IS_PRA_ARROW_WITH_BACKGROUND_DISABLED === 'enabled';
    const isControlWithChevron = process.env.IS_PRA_CONTROL_WITH_CHEVRON_DISABLED === 'enabled';
    const isBleedOutEnabled = process.env.IS_PRA_CAROUSEL_BLEEDOUT_DISABLED === 'enabled';
    const isArrowAlignedToImage = process.env.IS_PRA_ARROW_ALIGNED_TO_IMAGE === 'enabled';
    const isArrowWithSmallBackground = process.env.IS_PRA_ARROW_WITH_SMALL_BACKGROUND === 'enabled';
    const isInfinityScrollEnabled = process.env.IS_PRA_WITH_INFINITY_SCROLL === 'enabled';
    const arrowsToTopRight = process.env.IS_PRA_WITH_ARROWS_TO_TOP_RIGHT === 'enabled';
    const isProductTileDetailsCentreAligned = process.env.IS_PRODUCT_TILE_DETAILS_CENTRE_ALIGNED === 'enabled';
    const [topRightCarouselControls, setTopRightCarouselControls] = useState(arrowsToTopRight);
    const [visibleItemsInCarousel, setVisibleItemsInCarousel] = useState(NUM_OF_ITEMS_PER_SLIDE[breakpoint]);
    const [lastIndex, setLastIndex] = useState(null);
    const [clickParams, setClickParams] = useState();
    const [dragStart, setDragStart] = useState(0);
    const [dragEnd, setDragEnd] = useState(0);
    // TODO: remove when API sourced labels available for brands
    if (textNext && textPrev) {
        uiLabels.next = textNext;
        uiLabels.prev = textPrev;
    }
    const productClick = (params) => {
        if (BRAND === InternalBrandIds.HM) {
            handleClick(params);
        }
        else {
            handleClickForBrand(params);
        }
    };
    const impression = (event, onShow) => {
        if (!isLoading) {
            const params = Object.assign(Object.assign(Object.assign(Object.assign({}, initialImpressionParams), { productSet: finalProducts, visibleItemsInCarousel,
                trackedProductsOnImpression, articleId: articleId || ID }), (onShow && { onShow })), (event && { event }));
            const latestTrackedArticles = triggerCarouselImpression(params);
            setTrackedProductsOnImpression(latestTrackedArticles);
            if (setTrackedArticles) {
                setTrackedArticles(latestTrackedArticles);
            }
        }
    };
    // Only for A/B testing purposes
    // TODO: remove when iterative testing of component is completed. PXREC-2968
    const handleFavouritesClick = (isFav) => {
        let eventName;
        if (ID === 'PRA1') {
            eventName = AbEvents.ON_ATF_OTHERS_ALSO_BOUGHT_CLICK;
        }
        else if (ID === 'SWG') {
            eventName = AbEvents.ON_ATF_STYLE_WITH_CLICK;
        }
        else if (ID === 'PRA2') {
            eventName = AbEvents.ON_ATF_RELATED_ARTICLES_CLICK;
        }
        if (isFav && eventName) {
            trackOptimizelyVanillaEvent(eventName);
        }
    };
    useEffect(() => {
        const amountOfVisualSlidesForBreakpoint = NUM_OF_ITEMS_PER_SLIDE[breakpoint];
        if (amountOfVisualSlidesForBreakpoint !== visibleItemsInCarousel) {
            setVisibleItemsInCarousel(amountOfVisualSlidesForBreakpoint);
            if (finalProducts && arrowsToTopRight) {
                setTopRightCarouselControls(finalProducts.length > amountOfVisualSlidesForBreakpoint && arrowsToTopRight);
            }
        }
    }, [breakpoint]);
    useEffect(() => {
        if (lastIndex !== null) {
            impression(lastIndex, false);
        }
    }, [visibleItemsInCarousel]);
    useEffect(() => {
        if (carouselIsShown && (finalProducts === null || finalProducts === void 0 ? void 0 : finalProducts.length)) {
            impression(false, true);
            setLastIndex(0);
            if (arrowsToTopRight) {
                setTopRightCarouselControls(!(finalProducts.length <= visibleItemsInCarousel));
            }
        }
    }, [carouselIsShown, finalProducts]);
    useEffect(() => {
        if (trackedArticles) {
            setTrackedProductsOnImpression(trackedArticles);
        }
        const element = document.querySelector(`.${ID.toLowerCase()}`);
        if (element) {
            element.addEventListener('mouseup', (e) => {
                setDragEnd(e.pageX);
            });
            element.addEventListener('mousedown', (e) => {
                setDragStart(e.pageX);
            });
        }
    }, []);
    useEffect(() => {
        if (!isTouchDevice() && clickParams && Math.abs(dragStart - dragEnd) < 6) {
            productClick(clickParams);
        }
    }, [clickParams]);
    if (!finalProducts || finalProducts.length === 0) {
        return null;
    }
    return (React.createElement(IntersectionVisible, { className: className, onShow: () => {
            setCarouselIsShown(true);
        }, onHide: () => {
            setCarouselIsShown(false);
        }, active: true, options: {
            threshold: THRESHOLD || 0.1,
        } },
        React.createElement(ProductCarousel, { key: `product-reco-${ID}-${articleId || 'default'}`, onAfterChange: (e) => {
                if (carouselIsShown) {
                    impression(e, false);
                }
            }, linkClickCallback: (e) => {
                const params = {
                    event: e,
                    productSet: finalProducts,
                    modelInfo,
                    praConfiguration,
                    origin,
                    carouselTitle,
                };
                if (isTouchDevice()) {
                    productClick(params);
                }
                else {
                    setClickParams(params);
                }
            }, title: carouselTitle, products: finalProducts, labels: uiLabels, numOfItemsPerSlide: NUM_OF_ITEMS_PER_SLIDE, useModelAsDefault: (imageType && imageType.toLowerCase() !== constants.IMAGE_TYPES.PRODUCT) || false, className: cn(ID.toLowerCase(), BRAND === InternalBrandIds.COS && styles.cosAdjustment), isInfinite: isInfinityScrollEnabled, showDotsWithArrows: showDotsWithArrows, arrowsToTopRight: topRightCarouselControls, noPadding: disablePadding, onlyArrows: arrowsToTopRight, isTitleCenterAligned: isTitleCenterAligned, isDotsOnTop: isDotsAboveCarousel, isArrowWithBackground: isArrowWithBackground, isControlWithChevron: isControlWithChevron, isBleedOutEnabled: isBleedOutEnabled, isArrowAlignedToImage: isArrowAlignedToImage, isArrowWithSmallBackground: isArrowWithSmallBackground, titleSize: HEADING_SIZE, shouldProtectSliderClick: !(LAZYLOAD_IMAGES || process.env.IS_PRA_LP_RESOLVER_ENABLED === 'enabled'), isProductTileDetailsCentreAligned: isProductTileDetailsCentreAligned, lazyLoad: process.env.IS_PRA_LP_RESOLVER_ENABLED === 'enabled' ? 'progressive' : undefined, descriptionFontSize: DESCRIPTION_SIZE || undefined, reserveHeightAspectRatio: resolverSettings && `${resolverSettings.WIDTH}/${resolverSettings.HEIGHT}`, shouldTruncateProductTitle: PRODUCT_TITLE_TRUNCATE && false, shouldRenderEmptySlides: CENTER_ALIGNED ? false : undefined, shouldInitWithSingleBreakpoint: true, onFavouritesClick: handleFavouritesClick, HeadingTag: BRAND === InternalBrandIds.HM ? 'h2' : 'h3', shouldUseDirectSrc: true, carouselHeadingClassName: carouselHeadingClassName })));
};
export default Carousel;
//# sourceMappingURL=CarouselV1.js.map